import { OutputType } from "../../enums.js";
import {
  AppSessionCellId,
  OutputContentId,
  OutputId,
} from "../../idTypeBrands.js";
import { createAppSessionEventDefinition } from "../AppSessionEventDefinition.js";
import { AppSessionEventState } from "../AppSessionEventState.js";

export const CLEAR_OUTPUTS = createAppSessionEventDefinition({
  type: "CLEAR_OUTPUTS",
  logSafe: {
    appSessionCellId: true,
    requestId: true,
    preserveMimeTypes: true,
    clearMetadata: true,
    clearNonMetadata: true,
  },
  create: (payload: {
    appSessionCellId: AppSessionCellId;
    /**
     * If present, skip deleting outputs with this requestId
     */
    requestId?: string;
    /**
     * If present, skip deleting any outputs that have a content with this mime type
     */
    preserveMimeTypes?: readonly string[];
    /** @default true */
    clearMetadata?: boolean;
    /** @default true */
    clearNonMetadata?: boolean;
  }) => payload,
  apply: (previousState, payload) => {
    const { outputContentIdsToDelete, outputIdsToDelete } =
      findEventOutputsToDelete({
        state: previousState,
        ...payload,
      });

    for (const outputId of outputIdsToDelete) {
      delete previousState.outputs[outputId];
    }

    for (const outputContentIds of Object.values(outputContentIdsToDelete)) {
      for (const outputContentId of outputContentIds) {
        delete previousState.outputContents[outputContentId];
      }
    }
  },
});

export function findEventOutputsToDelete({
  appSessionCellId,
  clearMetadata = true,
  clearNonMetadata = true,
  preserveMimeTypes,
  requestId,
  state,
}: {
  state: AppSessionEventState;
  appSessionCellId: AppSessionCellId;
  requestId?: string;
  preserveMimeTypes?: readonly string[];
  clearMetadata?: boolean;
  clearNonMetadata?: boolean;
}): {
  outputIdsToDelete: Set<OutputId>;
  outputContentIdsToDelete: Record<OutputId, Set<OutputContentId>>;
} {
  const preserveMimeTypesSet = new Set(preserveMimeTypes);
  const outputIdsToMaybeDelete: Set<OutputId> = new Set();

  for (const output of Object.values(state.outputs)) {
    if (output.appSessionCellId !== appSessionCellId) {
      continue;
    }

    if (requestId != null && output.requestId === requestId) {
      continue;
    }

    const isMetadata = output.outputType === OutputType.METADATA;

    if ((isMetadata && !clearMetadata) || (!isMetadata && !clearNonMetadata)) {
      continue;
    }

    outputIdsToMaybeDelete.add(output.id);
  }

  const outputIdsToDelete: Set<OutputId> = new Set(outputIdsToMaybeDelete);
  const outputContentIdsToDelete: Record<OutputId, Set<OutputContentId>> = {};

  for (const outputContent of Object.values(state.outputContents)) {
    if (!outputIdsToMaybeDelete.has(outputContent.outputId)) {
      continue;
    }

    if (preserveMimeTypesSet.has(outputContent.mimeType)) {
      outputIdsToDelete.delete(outputContent.outputId);
      continue;
    }

    const prev = (outputContentIdsToDelete[outputContent.outputId] ??=
      new Set());
    prev.add(outputContent.id);
  }

  return { outputIdsToDelete, outputContentIdsToDelete };
}
